﻿<template>
    <div style="padding: 0 30px">
        <el-card shadow="never" style="margin-bottom: 20px">
            <template #header>
                <div style="display: flex; justify-content: space-between; align-items: center">
                    <span>LTV预测配置</span>
                </div>
            </template>
            <cohort-form :loading="loading" ref="cohort" @submit="init" :previous="60" dimensions />
            
            <el-form label-width="120px" style="margin-top: 20px">
                <el-form-item label="预测模型">
                    <el-select v-model="modelType" placeholder="选择预测模型" style="width: 200px">
                        <el-option
                            v-for="model in availableModels"
                            :key="model.value"
                            :label="model.label"
                            :value="model.value">
                        </el-option>
                    </el-select>
                    <span style="margin-left: 10px; color: #909399">不同模型适用于不同的数据特征</span>
                </el-form-item>
                <el-form-item label="融合权重" v-if="modelType === 'ensemble'">
                    <el-slider v-model="ensembleWeight" :min="0" :max="1" :step="0.05"
                               :format-tooltip="v => `Weibull ${Math.round(v*100)}% / Power ${Math.round((1-v)*100)}%`"
                               style="width: 300px; display: inline-block; margin-right: 15px" />
                    <span style="color: #606266">Weibull {{ Math.round(ensembleWeight*100) }}% + Power {{ Math.round((1-ensembleWeight)*100) }}%</span>
                </el-form-item>
                <el-form-item label="训练天数">
                    <el-input-number v-model="trainDays" :min="15" :max="60" :step="5" />
                    <span style="margin-left: 10px; color: #909399">使用前N天数据训练模型</span>
                </el-form-item>
                <el-form-item label="最小安装量">
                    <el-input-number v-model="minInstalls" :min="10" :max="10000" :step="10" />
                </el-form-item>
            </el-form>
        </el-card>

        <div v-loading="loading">
            <el-card shadow="never" style="margin-bottom: 20px">
                <template #header>
                    <div style="display: flex; justify-content: space-between; align-items: center">
                        <span>预测曲线对比 - {{ currentModelName }}</span>
                        <el-radio-group v-model="chartMode" size="small" @change="redrawChart">
                            <el-radio-button label="roi">ROI曲线</el-radio-button>
                            <el-radio-button label="ltv">LTV曲线</el-radio-button>
                        </el-radio-group>
                    </div>
                </template>
                <div id="chart" style="height: 500px; width: 100%;"></div>
            </el-card>

            <el-card shadow="never">
                <template #header>
                    <span>预测数据详情</span>
                </template>
                <el-table :data="data" stripe border>
                    <el-table-column v-for="dim in dims" :key="dim" :prop="dim" :label="dim" 
                                     width="120" sortable show-overflow-tooltip align="center">
                        <template #default="scope">
                            <span>{{ scope.row[dim] || '未归因' }}</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="安装量" prop="installs" width="100" sortable align="center" />
                    
                    <!-- ROI30 在 LTV30 前面 -->
                    <el-table-column label="ROI30真实" prop="roi30_real" width="100" sortable align="center">
                        <template #default="scope">
                            <el-tooltip placement="top">
                                <template #content>
                                    <div>天数: 30</div>
                                    <div>LTV真实值: {{ formatValue(scope.row.lt30_real) }}</div>
                                    <div>LTV预测值: {{ formatValue(scope.row.lt30_predict) }}</div>
                                    <div>ROI真实值: {{ formatROI(scope.row.roi30_real) }}</div>
                                    <div>ROI预测值: {{ formatROI(scope.row.roi30_predict) }}</div>
                                </template>
                                <span>{{ formatROI(scope.row.roi30_real) }}</span>
                            </el-tooltip>
                        </template>
                    </el-table-column>
                    <el-table-column label="ROI30预测" prop="roi30_predict" width="100" sortable align="center">
                        <template #default="scope">
                            <el-tooltip placement="top">
                                <template #content>
                                    <div>天数: 30</div>
                                    <div>LTV真实值: {{ formatValue(scope.row.lt30_real) }}</div>
                                    <div>LTV预测值: {{ formatValue(scope.row.lt30_predict) }}</div>
                                    <div>ROI真实值: {{ formatROI(scope.row.roi30_real) }}</div>
                                    <div>ROI预测值: {{ formatROI(scope.row.roi30_predict) }}</div>
                                </template>
                                <span style="color: #409EFF">{{ formatROI(scope.row.roi30_predict) }}</span>
                            </el-tooltip>
                        </template>
                    </el-table-column>
                    <el-table-column label="LTV30真实" prop="lt30_real" width="100" sortable align="center">
                        <template #default="scope">
                            <el-tooltip placement="top">
                                <template #content>
                                    <div>天数: 30</div>
                                    <div>LTV真实值: {{ formatValue(scope.row.lt30_real) }}</div>
                                    <div>LTV预测值: {{ formatValue(scope.row.lt30_predict) }}</div>
                                    <div>ROI真实值: {{ formatROI(scope.row.roi30_real) }}</div>
                                    <div>ROI预测值: {{ formatROI(scope.row.roi30_predict) }}</div>
                                </template>
                                <span>{{ formatValue(scope.row.lt30_real) }}</span>
                            </el-tooltip>
                        </template>
                    </el-table-column>
                    <el-table-column label="LTV30预测" prop="lt30_predict" width="100" sortable align="center">
                        <template #default="scope">
                            <el-tooltip placement="top">
                                <template #content>
                                    <div>天数: 30</div>
                                    <div>LTV真实值: {{ formatValue(scope.row.lt30_real) }}</div>
                                    <div>LTV预测值: {{ formatValue(scope.row.lt30_predict) }}</div>
                                    <div>ROI真实值: {{ formatROI(scope.row.roi30_real) }}</div>
                                    <div>ROI预测值: {{ formatROI(scope.row.roi30_predict) }}</div>
                                </template>
                                <span style="color: #409EFF">{{ formatValue(scope.row.lt30_predict) }}</span>
                            </el-tooltip>
                        </template>
                    </el-table-column>

                    <!-- ROI60 在 LTV60 前面 -->
                    <el-table-column label="ROI60真实" prop="roi60_real" width="100" sortable align="center">
                        <template #default="scope">
                            <el-tooltip placement="top">
                                <template #content>
                                    <div>天数: 60</div>
                                    <div>LTV真实值: {{ formatValue(scope.row.lt60_real) }}</div>
                                    <div>LTV预测值: {{ formatValue(scope.row.lt60_predict) }}</div>
                                    <div>ROI真实值: {{ formatROI(scope.row.roi60_real) }}</div>
                                    <div>ROI预测值: {{ formatROI(scope.row.roi60_predict) }}</div>
                                </template>
                                <span>{{ formatROI(scope.row.roi60_real) }}</span>
                            </el-tooltip>
                        </template>
                    </el-table-column>
                    <el-table-column label="ROI60预测" prop="roi60_predict" width="100" sortable align="center">
                        <template #default="scope">
                            <el-tooltip placement="top">
                                <template #content>
                                    <div>天数: 60</div>
                                    <div>LTV真实值: {{ formatValue(scope.row.lt60_real) }}</div>
                                    <div>LTV预测值: {{ formatValue(scope.row.lt60_predict) }}</div>
                                    <div>ROI真实值: {{ formatROI(scope.row.roi60_real) }}</div>
                                    <div>ROI预测值: {{ formatROI(scope.row.roi60_predict) }}</div>
                                </template>
                                <span style="color: #409EFF">{{ formatROI(scope.row.roi60_predict) }}</span>
                            </el-tooltip>
                        </template>
                    </el-table-column>
                    <el-table-column label="LTV60真实" prop="lt60_real" width="100" sortable align="center">
                        <template #default="scope">
                            <el-tooltip placement="top">
                                <template #content>
                                    <div>天数: 60</div>
                                    <div>LTV真实值: {{ formatValue(scope.row.lt60_real) }}</div>
                                    <div>LTV预测值: {{ formatValue(scope.row.lt60_predict) }}</div>
                                    <div>ROI真实值: {{ formatROI(scope.row.roi60_real) }}</div>
                                    <div>ROI预测值: {{ formatROI(scope.row.roi60_predict) }}</div>
                                </template>
                                <span>{{ formatValue(scope.row.lt60_real) }}</span>
                            </el-tooltip>
                        </template>
                    </el-table-column>
                    <el-table-column label="LTV60预测" prop="lt60_predict" width="100" sortable align="center">
                        <template #default="scope">
                            <el-tooltip placement="top">
                                <template #content>
                                    <div>天数: 60</div>
                                    <div>LTV真实值: {{ formatValue(scope.row.lt60_real) }}</div>
                                    <div>LTV预测值: {{ formatValue(scope.row.lt60_predict) }}</div>
                                    <div>ROI真实值: {{ formatROI(scope.row.roi60_real) }}</div>
                                    <div>ROI预测值: {{ formatROI(scope.row.roi60_predict) }}</div>
                                </template>
                                <span style="color: #409EFF">{{ formatValue(scope.row.lt60_predict) }}</span>
                            </el-tooltip>
                        </template>
                    </el-table-column>

                    <!-- ROI90 在 LTV90 前面 -->
                    <el-table-column label="ROI90真实" prop="roi90_real" width="100" sortable align="center">
                        <template #default="scope">
                            <el-tooltip placement="top">
                                <template #content>
                                    <div>天数: 90</div>
                                    <div>LTV真实值: {{ formatValue(scope.row.lt90_real) }}</div>
                                    <div>LTV预测值: {{ formatValue(scope.row.lt90_predict) }}</div>
                                    <div>ROI真实值: {{ formatROI(scope.row.roi90_real) }}</div>
                                    <div>ROI预测值: {{ formatROI(scope.row.roi90_predict) }}</div>
                                </template>
                                <span>{{ formatROI(scope.row.roi90_real) }}</span>
                            </el-tooltip>
                        </template>
                    </el-table-column>
                    <el-table-column label="ROI90预测" prop="roi90_predict" width="100" sortable align="center">
                        <template #default="scope">
                            <el-tooltip placement="top">
                                <template #content>
                                    <div>天数: 90</div>
                                    <div>LTV真实值: {{ formatValue(scope.row.lt90_real) }}</div>
                                    <div>LTV预测值: {{ formatValue(scope.row.lt90_predict) }}</div>
                                    <div>ROI真实值: {{ formatROI(scope.row.roi90_real) }}</div>
                                    <div>ROI预测值: {{ formatROI(scope.row.roi90_predict) }}</div>
                                </template>
                                <span style="color: #409EFF">{{ formatROI(scope.row.roi90_predict) }}</span>
                            </el-tooltip>
                        </template>
                    </el-table-column>
                    <el-table-column label="LTV90真实" prop="lt90_real" width="100" sortable align="center">
                        <template #default="scope">
                            <el-tooltip placement="top">
                                <template #content>
                                    <div>天数: 90</div>
                                    <div>LTV真实值: {{ formatValue(scope.row.lt90_real) }}</div>
                                    <div>LTV预测值: {{ formatValue(scope.row.lt90_predict) }}</div>
                                    <div>ROI真实值: {{ formatROI(scope.row.roi90_real) }}</div>
                                    <div>ROI预测值: {{ formatROI(scope.row.roi90_predict) }}</div>
                                </template>
                                <span>{{ formatValue(scope.row.lt90_real) }}</span>
                            </el-tooltip>
                        </template>
                    </el-table-column>
                    <el-table-column label="LTV90预测" prop="lt90_predict" width="100" sortable align="center">
                        <template #default="scope">
                            <el-tooltip placement="top">
                                <template #content>
                                    <div>天数: 90</div>
                                    <div>LTV真实值: {{ formatValue(scope.row.lt90_real) }}</div>
                                    <div>LTV预测值: {{ formatValue(scope.row.lt90_predict) }}</div>
                                    <div>ROI真实值: {{ formatROI(scope.row.roi90_real) }}</div>
                                    <div>ROI预测值: {{ formatROI(scope.row.roi90_predict) }}</div>
                                </template>
                                <span style="color: #409EFF">{{ formatValue(scope.row.lt90_predict) }}</span>
                            </el-tooltip>
                        </template>
                    </el-table-column>

                    <!-- ROI120 单独，没有对应的LTV -->
                    <el-table-column label="ROI120真实" prop="roi120_real" width="100" sortable align="center">
                        <template #default="scope">
                            <el-tooltip placement="top">
                                <template #content>
                                    <div>天数: 120</div>
                                    <div>LTV真实值: {{ formatValue(scope.row.lt120_real) }}</div>
                                    <div>LTV预测值: {{ formatValue(scope.row.lt120_predict) }}</div>
                                    <div>ROI真实值: {{ formatROI(scope.row.roi120_real) }}</div>
                                    <div>ROI预测值: {{ formatROI(scope.row.roi120_predict) }}</div>
                                </template>
                                <span>{{ formatROI(scope.row.roi120_real) }}</span>
                            </el-tooltip>
                        </template>
                    </el-table-column>
                    <el-table-column label="ROI120预测" prop="roi120_predict" width="100" sortable align="center">
                        <template #default="scope">
                            <el-tooltip placement="top">
                                <template #content>
                                    <div>天数: 120</div>
                                    <div>LTV真实值: {{ formatValue(scope.row.lt120_real) }}</div>
                                    <div>LTV预测值: {{ formatValue(scope.row.lt120_predict) }}</div>
                                    <div>ROI真实值: {{ formatROI(scope.row.roi120_real) }}</div>
                                    <div>ROI预测值: {{ formatROI(scope.row.roi120_predict) }}</div>
                                </template>
                                <span style="color: #409EFF">{{ formatROI(scope.row.roi120_predict) }}</span>
                            </el-tooltip>
                        </template>
                    </el-table-column>

                    <!-- ROI180 在 LTV180 前面 -->
                    <el-table-column label="ROI180真实" prop="roi180_real" width="100" sortable align="center">
                        <template #default="scope">
                            <el-tooltip placement="top">
                                <template #content>
                                    <div>天数: 180</div>
                                    <div>LTV真实值: {{ formatValue(scope.row.lt180_real) }}</div>
                                    <div>LTV预测值: {{ formatValue(scope.row.lt180_predict) }}</div>
                                    <div>ROI真实值: {{ formatROI(scope.row.roi180_real) }}</div>
                                    <div>ROI预测值: {{ formatROI(scope.row.roi180_predict) }}</div>
                                </template>
                                <span>{{ formatROI(scope.row.roi180_real) }}</span>
                            </el-tooltip>
                        </template>
                    </el-table-column>
                    <el-table-column label="ROI180预测" prop="roi180_predict" width="100" sortable align="center">
                        <template #default="scope">
                            <el-tooltip placement="top">
                                <template #content>
                                    <div>天数: 180</div>
                                    <div>LTV真实值: {{ formatValue(scope.row.lt180_real) }}</div>
                                    <div>LTV预测值: {{ formatValue(scope.row.lt180_predict) }}</div>
                                    <div>ROI真实值: {{ formatROI(scope.row.roi180_real) }}</div>
                                    <div>ROI预测值: {{ formatROI(scope.row.roi180_predict) }}</div>
                                </template>
                                <span style="color: #409EFF">{{ formatROI(scope.row.roi180_predict) }}</span>
                            </el-tooltip>
                        </template>
                    </el-table-column>
                    <el-table-column label="LTV180真实" prop="lt180_real" width="100" sortable align="center">
                        <template #default="scope">
                            <el-tooltip placement="top">
                                <template #content>
                                    <div>天数: 180</div>
                                    <div>LTV真实值: {{ formatValue(scope.row.lt180_real) }}</div>
                                    <div>LTV预测值: {{ formatValue(scope.row.lt180_predict) }}</div>
                                    <div>ROI真实值: {{ formatROI(scope.row.roi180_real) }}</div>
                                    <div>ROI预测值: {{ formatROI(scope.row.roi180_predict) }}</div>
                                </template>
                                <span>{{ formatValue(scope.row.lt180_real) }}</span>
                            </el-tooltip>
                        </template>
                    </el-table-column>
                    <el-table-column label="LTV180预测" prop="lt180_predict" width="100" sortable align="center">
                        <template #default="scope">
                            <el-tooltip placement="top">
                                <template #content>
                                    <div>天数: 180</div>
                                    <div>LTV真实值: {{ formatValue(scope.row.lt180_real) }}</div>
                                    <div>LTV预测值: {{ formatValue(scope.row.lt180_predict) }}</div>
                                    <div>ROI真实值: {{ formatROI(scope.row.roi180_real) }}</div>
                                    <div>ROI预测值: {{ formatROI(scope.row.roi180_predict) }}</div>
                                </template>
                                <span style="color: #409EFF">{{ formatValue(scope.row.lt180_predict) }}</span>
                            </el-tooltip>
                        </template>
                    </el-table-column>
                </el-table>
            </el-card>
        </div>
    </div>
</template>


<script>
import CohortForm from "../../base/CohortForm";
import axios from 'ts-axios-new';

export default {
    name: "LTVPredict",
    components: {CohortForm},
    data() {
        return {
            loading: false,
            data: [],
            dims: [],
            trainDays: 30,
            minInstalls: 100,
            modelType: 'weibull',  // weibull作为默认模型
            ensembleWeight: 0.5,
            chartMode: 'roi',  // 'roi' or 'ltv'
            lastChartData: null,
            lastChartDims: null,
            lastTrainDays: null,
            availableModels: [],
            currentModelName: '',
        }
    },
    methods: {
        formatValue(val) {
            if (val === null || val === undefined) return '-';
            // Format as currency (USD)
            return typeof val === 'number' ? '$' + val.toFixed(4) : val;
        },
        
        formatROI(val) {
            if (val === null || val === undefined) return '-';
            // Format as ratio with 2 decimal places
            return typeof val === 'number' ? val.toFixed(2) : val;
        },
        
        async loadModels() {
            try {
                const res = await axios.get(`/api/v1/ltv/predict`);
                this.availableModels = res.data.data.models;
            } catch (err) {
                console.error('Failed to load models:', err);
                // Fallback to default models
                this.availableModels = [
                    { value: 'log', label: 'Log模型' },
                    { value: 'power', label: 'Power模型' },
                    { value: 'exponential', label: 'Exponential模型' },
                    { value: 'weibull', label: 'Weibull模型' },
                ];
            }
        },
        
        init() {
            this.loading = true;
            const form = this.$refs.cohort.getData();
            form.product_id = this.$route.params.productId;
            form.train_days = this.trainDays;
            form.min_installs = this.minInstalls;
            form.model_type = this.modelType;
            form.ensemble_weight = this.ensembleWeight;
            
            axios.post(`/api/v1/ltv/predict`, form).then(res => {
                const result = res.data.data;
                this.data = result.data;
                this.dims = form.dimensions;
                this.currentModelName = result.model_name || this.modelType;
                
                // 保存数据供切换图表模式时重绘
                this.lastChartData = result.data;
                this.lastChartDims = form.dimensions;
                this.lastTrainDays = result.train_days;
                
                // 准备图表数据
                this.renderChart(result.data, form.dimensions, result.train_days);
                
                this.loading = false;
            }).catch(err => {
                this.$message.error('预测失败: ' + (err.response?.data?.status?.message || err.message));
                this.loading = false;
            });
        },
        
        redrawChart() {
            if (this.lastChartData) {
                this.renderChart(this.lastChartData, this.lastChartDims, this.lastTrainDays);
            }
        },

        renderChart(data, dimensions, trainDays) {
            const series = [];
            const isROI = this.chartMode === 'roi';
            const maxLen = Math.max(...data.map(d => d.roi_real?.length || d.real?.length || 0));
            const x = Array.from({length: maxLen}, (_, i) => i);
            
            data.forEach((d, idx) => {
                // 构建维度标签
                let name = '';
                dimensions.forEach(dim => {
                    name += (d[dim] || '未归因') + ' ';
                });
                name = name.trim() || `群组${idx + 1}`;
                
                // 真实数据 - 根据模式切换纵轴值
                const realData = (d.real || []).map((v, i) => ({
                    value: isROI ? (d.roi_real?.[i] ?? null) : v,
                    ltv: v,
                    roi: d.roi_real?.[i] ?? null
                })); 
                
                // 预测数据 - 根据模式切换纵轴值
                const predictData = (d.predict || []).map((v, i) => ({
                    value: isROI ? (d.roi_predict?.[i] ?? 0) : (v ?? 0),
                    ltv: v,
                    roi: d.roi_predict?.[i] ?? 0
                }));
                
                // 真实曲线
                series.push({
                    name: name + ' 真实',
                    type: 'line',
                    data: realData,
                    smooth: true,
                    lineStyle: { width: 2 },
                    itemStyle: { color: '#67C23A' },
                });
                
                // 预测曲线
                series.push({
                    name: name + ' 预测',
                    type: 'line',
                    data: predictData,
                    smooth: true,
                    lineStyle: { 
                        width: 2,
                        type: 'dashed'
                    },
                    itemStyle: { color: '#409EFF' },
                });
            });
            
            // 添加训练期结束标记线
            if (series.length > 0) {
                series[0].markLine = {
                    silent: true,
                    symbol: 'none',
                    label: {
                        position: 'end',
                        formatter: '训练期结束'
                    },
                    lineStyle: {
                        color: '#E6A23C',
                        type: 'solid',
                        width: 2
                    },
                    data: [{
                        xAxis: trainDays,
                    }]
                };
            }
            
            echarts.init(document.getElementById('chart')).setOption({
                title: {
                    text: `${isROI ? 'ROI' : 'LTV'}预测对比 - ${this.currentModelName}`,
                    left: 'center'
                },
                xAxis: {
                    type: 'category',
                    data: x,
                    name: '天数',
                    nameLocation: 'middle',
                    nameGap: 30,
                },
                yAxis: {
                    type: 'value',
                    name: isROI ? 'ROI' : '累积ARPU (USD)',
                    nameLocation: 'middle',
                    nameGap: 50,
                },
                legend: {
                    type: 'scroll',
                    bottom: 10,
                },
                tooltip: {
                    trigger: 'axis',
                    formatter: function(params) {
                        if (!params || params.length === 0) return '';
                        
                        const day = params[0].axisValue;
                        let html = '<div style="padding: 5px;">';
                        html += '<div style="font-weight: bold; margin-bottom: 8px;">天数: ' + day + '</div>';
                        
                        params.forEach(function(param) {
                            if (param.data && param.data.value !== null && param.data.value !== undefined) {
                                html += '<div style="margin: 4px 0;">';
                                html += param.marker + '<span style="font-weight: bold;">' + param.seriesName + '</span><br/>';
                                // 显示LTV（原始值）和ROI（当前纵轴值）
                                html += '  ROI: ' + (param.data.roi?.toFixed(2) ?? '0.00');
                                html += '<br/>  LTV: $' + (param.data.ltv?.toFixed(5) ?? '0.00000');
                                html += '</div>';
                            }
                        });
                        
                        html += '</div>';
                        return html;
                    }
                },
                grid: {
                    left: '3%',
                    right: '4%',
                    bottom: '15%',
                    containLabel: true
                },
                series
            }, true);
        }
    },
    async mounted() {
        await this.loadModels();
        this.init();
    }
}
</script>

<style scoped>
.el-form-item {
    margin-bottom: 18px;
}
</style>
